import React, { useRef, useEffect } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, IconButton, Typography, MenuItem, Stack, FormControlLabel, Switch } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import { FormFieldType } from '@/core/models/form.model'
import { Trans, t } from '@lingui/macro'
import { DEFAULT_FORM_CATEGORIES, DEFAULT_FORM_TAGS } from '../constants'
import { getCategoryLabel, getTagLabel } from '../vocabularyLabels'
import { useLanguage } from '@/hooks/useLanguage'
import { useSnackbar } from 'notistack'
import VocabularyPicker from './VocabularyPicker'
import { withChecklistCategory } from '../lib/formTemplateClassification'

interface Field {
  label: string
  type: FormFieldType
  required: boolean
  options?: string[]
}

interface CreateFormTemplateDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: { title: string; description: string; fields: Field[]; categories: string[]; tags: string[] }) => void | Promise<void>
  isSubmitting?: boolean
}

const labelSx = { display: 'block', fontWeight: 600, fontSize: '0.875rem', color: 'text.primary', mb: 1 } as const
const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } } as const

const CreateFormTemplateDialogComponent: React.FC<CreateFormTemplateDialogProps> = ({ open, onClose, onSubmit, isSubmitting = false }) => {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [categories, setCategories] = React.useState<string[]>([])
  const [tags, setTags] = React.useState<string[]>([])
  // A "preparation checklist" is just a FormTemplate carrying the readiness-checklist category —
  // that category is exactly what the waiting-room readiness dropdown filters on.
  const [isChecklist, setIsChecklist] = React.useState(false)
  const [fields, setFields] = React.useState<Field[]>([])
  const newFieldRef = useRef<HTMLDivElement>(null)
  const shouldScrollToNewField = useRef(false)
  const lang = useLanguage()
  const { enqueueSnackbar } = useSnackbar()

  const handleAddField = () => {
    shouldScrollToNewField.current = true
    setFields([
      ...fields,
      {
        label: '',
        type: FormFieldType.Text,
        required: false,
        options: [],
      },
    ])
  }

  const handleRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (shouldScrollToNewField.current && newFieldRef.current) {
      newFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      shouldScrollToNewField.current = false
    }
  }, [fields])

  const handleFieldChange = (index: number, field: Partial<Field>) => {
    setFields(
      fields.map((f, i) => {
        if (i !== index) return f

        const updatedField = { ...f, ...field }

        // Initialize or clear options when changing type
        if (field.type) {
          if (field.type === FormFieldType.Multiple) {
            updatedField.options = updatedField.options || []
          } else {
            updatedField.options = undefined
          }
        }

        return updatedField
      })
    )
  }

  const handleSubmit = async () => {
    try {
      await onSubmit({
        title,
        description,
        categories: withChecklistCategory(categories, isChecklist),
        tags,
        fields: fields.map((field) => ({ ...field, options: field.options?.map((option) => option.trim()).filter((option) => option.length > 0) })),
      })
      enqueueSnackbar(t`Form template created`, { variant: 'success' })
    } catch {
      enqueueSnackbar(t`Failed to save the form template. Please try again.`, { variant: 'error' })
    }
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    setCategories([])
    setTags([])
    setIsChecklist(false)
    setFields([])
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      {/* Header */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <AssignmentOutlinedIcon sx={{ color: 'primary.main' }} />
          <Typography component="span" sx={{ fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.01em' }}>
            {t`Create new form template`}
          </Typography>
        </Stack>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }} aria-label={t`Close`}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'background.default', p: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 2.5, sm: 4 },
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* Title */}
          <Box>
            <Typography component="label" sx={labelSx}>
              {t`Form title`}{' '}
              <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                *
              </Box>
            </Typography>
            <TextField fullWidth required value={title} onChange={(e) => setTitle(e.target.value)} sx={inputSx} />
          </Box>

          {/* Description */}
          <Box>
            <Typography component="label" sx={labelSx}>
              {t`Form description`}{' '}
              <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                *
              </Box>
            </Typography>
            <TextField fullWidth required value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={3} sx={inputSx} />
          </Box>

          <VocabularyPicker label={t`Categories`} options={DEFAULT_FORM_CATEGORIES} value={categories} onChange={setCategories} getLabel={(option) => getCategoryLabel(option, lang)} />

          <VocabularyPicker label={t`Tags`} options={DEFAULT_FORM_TAGS} value={tags} onChange={setTags} getLabel={(option) => getTagLabel(option, lang)} />

          {/* Waiting-room preparation checklist */}
          <Box>
            <FormControlLabel
              control={<Switch checked={isChecklist} onChange={(e) => setIsChecklist(e.target.checked)} />}
              label={<Trans>Use as a waiting-room preparation checklist</Trans>}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              <Trans>Checklist templates can be selected in a waiting room's readiness settings, so operators can tick items before the doctor joins.</Trans>
            </Typography>
          </Box>

          {/* Fields */}
          <Box sx={{ pt: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'text.primary', mb: 2 }}>
              {t`Fields`}{' '}
              <Box component="span" sx={{ color: 'error.main' }} aria-hidden>
                *
              </Box>
            </Typography>

            {fields.map((field, index) => (
              <Box
                key={index}
                ref={index === fields.length - 1 && shouldScrollToNewField.current ? newFieldRef : null}
                sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <TextField label={t`Field label`} value={field.label} onChange={(e) => handleFieldChange(index, { label: e.target.value })} size="small" fullWidth />
                  <TextField
                    select
                    label={t`Field type`}
                    value={field.type}
                    onChange={(e) => handleFieldChange(index, { type: e.target.value as FormFieldType })}
                    size="small"
                    sx={{ minWidth: 120 }}
                  >
                    {Object.values(FormFieldType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {t`${type}`}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton onClick={() => handleRemoveField(index)} color="error" size="small">
                    <DeleteIcon />
                  </IconButton>
                </Stack>

                {field.type === FormFieldType.Multiple && (
                  <TextField
                    fullWidth
                    label={t`Field options`}
                    value={field.options?.join('\n') || ''}
                    onChange={(e) =>
                      handleFieldChange(index, {
                        options: e.target.value.split('\n'),
                      })
                    }
                    helperText={t`Field options help`}
                    multiline
                    rows={4}
                    placeholder={t`Enter one option per line`}
                    size="small"
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddField}
              variant="outlined"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: 'primary.main', color: 'primary.main', '&:hover': { bgcolor: 'primary.main', borderColor: 'primary.main', color: 'primary.contrastText' } }}
            >
              <Trans>Add field</Trans>
            </Button>
          </Box>
        </Box>
      </DialogContent>

      {/* Action bar */}
      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2, bgcolor: 'background.paper', gap: 1 }}>
        <Button onClick={handleClose} sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'none', borderRadius: 2, px: 3 }}>
          {t`Cancel`}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !title || !description || fields.length === 0}
          sx={{
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            px: 4,
            py: 1,
            color: '#ffffff',
            background: 'linear-gradient(135deg, #621bcb 0%, #7b3fe4 100%)',
            boxShadow: '0 8px 20px rgba(98, 27, 203, 0.18)',
            '&:hover': { background: 'linear-gradient(135deg, #5a06c3 0%, #7334db 100%)', boxShadow: '0 10px 24px rgba(98, 27, 203, 0.24)' },
            '&.Mui-disabled': { background: (theme) => theme.palette.action.disabledBackground, color: (theme) => theme.palette.action.disabled, boxShadow: 'none' },
          }}
        >
          {isSubmitting ? t`Saving...` : t`Save`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateFormTemplateDialogComponent
